import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgZone } from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { HttpClient, HttpClientModule, HttpParams } from '@angular/common/http';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';

@Component({
  selector: 'app-foreign-college-images',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, HttpClientModule, AgGridAngular],
  templateUrl: './foreign-college-images.component.html',
  styleUrls: ['./foreign-college-images.component.scss'],
})
export class ForeignCollegeImagesComponent implements OnInit {
  display = 'none';
  imageForm!: FormGroup;
  countries: any[] = [];
  courses: any[] = [];
  colleges: any[] = [];
  selectedImages: string[] = [];
  previewImage: string | null = null;

  imagesList: any[] = [];

  // ✅ Popup states
  showSuccessPopup: boolean = false;
  showErrorPopup: boolean = false;
  popupMessage: string = '';

  defaultColDef: ColDef = { resizable: true, sortable: true, filter: true };

  // grid api
  gridApi: any;

  // track optimistic deleting (id or path)
  deletingImageId: string | number | null = null;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadImages();
    this.loadCountries();
  }

  buildForm() {
    this.imageForm = this.fb.group({
      country: ['', Validators.required],
      course: ['', Validators.required],
      college: ['', Validators.required],
    });
  }

  // -----------------------
  // Column defs: card with images + delete overlay
  // -----------------------
  columnDefs: ColDef[] = [
    {
      headerName: 'College & Images',
      field: 'images',
      flex: 1,
      autoHeight: true,
      cellRenderer: (params: any) => {
        const collegeId =
          params.data?.foreign_college_id ??
          params.data?.college_id ??
          params.data?.id;
        const collegeName = params.data?.college_name ?? 'College';
        const images = params.value || [];
        const expanded =
          (params.context &&
          params.context.componentParent &&
          params.context.componentParent.expandedRows
            ? params.context.componentParent.expandedRows[collegeId]
            : false) || false;

        const card = document.createElement('div');
        card.style.border = '1px solid #eee';
        card.style.borderRadius = '12px';
        card.style.padding = '15px';
        card.style.margin = '10px';
        card.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
        card.style.background = '#fff';

        // Title
        const title = document.createElement('h5');
        title.innerText = collegeName;
        title.style.fontWeight = '600';
        title.style.margin = '0 0 12px 0';
        card.appendChild(title);

        // Images container
        const imgContainer = document.createElement('div');
        imgContainer.style.display = 'flex';
        imgContainer.style.flexWrap = 'wrap';
        imgContainer.style.gap = '10px';

        const imagesToShow = expanded ? images : images.slice(0, 12);

        imagesToShow.forEach((imgData: any) => {
          if (!imgData?.image) return;

          const imgBox = document.createElement('div');
          imgBox.className = 'img-box';
          imgBox.style.position = 'relative'; // required for absolute overlay
          imgBox.style.width = '80px';
          imgBox.style.height = '80px';
          imgBox.style.borderRadius = '10px';
          imgBox.style.overflow = 'hidden';
          imgBox.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
          imgBox.style.cursor = 'pointer';
          imgBox.style.flex = '0 0 auto';

          const img = document.createElement('img');
          img.src = `https://smscap.in/${imgData.image}`;
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'cover';
          img.style.display = 'block';

          // preview on click (use ngZone to run back into Angular)
          img.addEventListener('click', (ev) => {
            ev.stopPropagation();
            this.ngZone.run(() => {
              this.openImagePreview(imgData.image);
            });
          });

          // Delete button (bottom-right) — transparent button, red trash icon
          const delBtn = document.createElement('button');
          delBtn.className = 'img-delete-btn';
          delBtn.type = 'button';
          delBtn.title = 'Delete image';
          delBtn.setAttribute('aria-label', 'Delete image');
          delBtn.innerHTML = '<i class="fa fa-trash" aria-hidden="true"></i>';

          // minimal inline fallback positioning to ensure it appears bottom-right
          delBtn.style.position = 'absolute';
          delBtn.style.bottom = '6px';
          delBtn.style.right = '6px';
          delBtn.style.zIndex = '5';
          delBtn.style.background = 'transparent';
          delBtn.style.border = 'none';
          delBtn.style.padding = '2px';
          delBtn.style.cursor = 'pointer';

          // stop preview when clicking delete
          delBtn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            const imgId =
              imgData.id ??
              imgData.image_id ??
              imgData.college_image_id ??
              imgData.imageId;
            this.ngZone.run(() => {
              // call component method (use params.context.componentParent if available)
              if (
                params.context &&
                params.context.componentParent &&
                params.context.componentParent.deleteImage
              ) {
                params.context.componentParent.deleteImage(
                  imgId,
                  params.data,
                  imgData
                );
              } else {
                this.deleteImage(imgId, params.data, imgData);
              }
            });
          });

          imgBox.appendChild(img);
          imgBox.appendChild(delBtn);
          imgContainer.appendChild(imgBox);
        });

        card.appendChild(imgContainer);

        // See more toggle if needed
        if (images.length > 12) {
          const toggle = document.createElement('button');
          toggle.innerText = expanded ? 'See Less' : 'See More';
          toggle.style.background = 'none';
          toggle.style.border = 'none';
          toggle.style.color = 'blue';
          toggle.style.cursor = 'pointer';
          toggle.style.marginTop = '8px';
          toggle.style.fontWeight = '500';
          toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            this.ngZone.run(() => {
              this.expandedRows[collegeId] = !this.expandedRows[collegeId];
              try {
                params.api.refreshCells({
                  rowNodes: [params.node],
                  columns: [params.column.getId()],
                  force: true,
                });
              } catch (err) {
                if (this.gridApi && (this.gridApi as any).refreshCells) {
                  try {
                    (this.gridApi as any).refreshCells({ force: true });
                  } catch (_) {}
                }
              }
            });
          });
          card.appendChild(toggle);
        }

        return card;
      },
    },
  ];

  // helper: track expanded rows
  expandedRows: { [key: number]: boolean } = {};

  // -----------------------
  // Data loaders / helpers
  // -----------------------
  loadCountries() {
    this.http.get<any>('https://smscap.in/api/country').subscribe({
      next: (res) => (this.countries = res?.data || []),
      error: (err) => {
        console.error('loadCountries error', err);
        this.countries = [];
      },
    });
  }

  onCountryChange(countryId: number) {
    this.courses = [];
    this.colleges = [];
    this.imageForm.patchValue({ course: '', college: '' });
    this.http
      .get<any>(`https://smscap.in/api/foreign_course/${countryId}`)
      .subscribe({
        next: (res) => (this.courses = res?.data || []),
        error: (err) => {
          console.error('onCountryChange error', err);
          this.courses = [];
        },
      });
  }

  onCourseChange(courseId: number) {
    const countryId = this.imageForm.value.country;
    this.colleges = [];
    this.imageForm.patchValue({ college: '' });
    this.http
      .get<any>(
        `https://smscap.in/api/foreign_college/${countryId}/${courseId}`
      )
      .subscribe({
        next: (res) => (this.colleges = res?.data || []),
        error: (err) => {
          console.error('onCourseChange error', err);
          this.colleges = [];
        },
      });
  }

  onFilesSelected(event: any) {
    const files = event.target.files;
    this.selectedImages = [];
    for (let file of files) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selectedImages.push(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  }

  addImages() {
    if (this.imageForm.invalid || this.selectedImages.length === 0) return;

    const payload = {
      foreign_college_id: this.imageForm.value.college,
      images: this.selectedImages,
    };

    this.http
      .post('https://smscap.in/api/foreign_college_image', payload)
      .subscribe({
        next: () => {
          this.popupMessage = 'Images added successfully ✅';
          this.showSuccessPopup = true;
          this.showErrorPopup = false;
          this.loadImages();
          this.imageForm.reset();
          this.selectedImages = [];
          this.onCloseHandled();

          setTimeout(() => this.closePopup(), 3000);
        },
        error: (err) => {
          console.error('Error adding images:', err);
          this.showSuccessPopup = false;
          if (err.status === 400 || err.status === 422) {
            this.popupMessage = 'Upload failed ❌';
          } else if (err.status === 500) {
            this.popupMessage = 'Internal Server Error ⚠️';
          } else {
            this.popupMessage = 'Something went wrong ❌';
          }
          this.showErrorPopup = true;
        },
      });
  }

  loadImages() {
    this.http
      .get<any>('https://smscap.in/api/foreign_college_image')
      .subscribe({
        next: (res) => {
          this.imagesList = res?.data || [];
          // refresh grid so cell renderers pick up new data
          if (this.gridApi && (this.gridApi as any).refreshCells) {
            try {
              (this.gridApi as any).refreshCells({ force: true });
            } catch (_) {}
          }
        },
        error: (err) => {
          console.error('loadImages error', err);
          this.imagesList = [];
        },
      });
  }

  openAdd() {
    this.display = 'block';
  }

  onCloseHandled() {
    this.display = 'none';
    this.imageForm.reset();
    this.selectedImages = [];
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  gridRefresh() {
    if (this.gridApi && (this.gridApi as any).refreshCells) {
      try {
        (this.gridApi as any).refreshCells({ force: true });
      } catch (_) {}
    }
  }

  openImagePreview(imagePath: string) {
    this.previewImage = `https://smscap.in/${imagePath}`;
  }

  closeImagePreview() {
    this.previewImage = null;
  }

  closePopup() {
    this.showSuccessPopup = false;
    this.showErrorPopup = false;
    this.popupMessage = '';
  }

  // -----------------------
  // Delete image: optimistic & immediate UI update
  // -----------------------
  deleteImage(imageId: any, parentRow: any, imageObj?: any) {
    const resolvedId =
      imageId ??
      imageObj?.id ??
      imageObj?.image_id ??
      imageObj?.college_image_id ??
      null;

    if (!resolvedId) {
      window.alert('Unable to determine image to delete.');
      return;
    }

    if (!window.confirm('Delete this image? This action cannot be undone.'))
      return;

    const params = new HttpParams().set('id', String(resolvedId));

    this.http
      .delete('https://smscap.in/api/foreign_college_image', { params })
      .subscribe({
        next: () => {
          // ✅ reload data from server immediately
          this.loadImages();

          // optional popup or toast
          this.popupMessage = 'Image deleted successfully ✅';
          this.showSuccessPopup = true;
          setTimeout(() => this.closePopup(), 2000);
        },
        error: (err) => {
          console.error('deleteImage error', err);
          this.popupMessage = 'Failed to delete image ❌';
          this.showErrorPopup = true;
        },
      });
  }
}
